// "Add to calendar" / "Remove from calendar" for one ToDo (mobile chatApi addTodoToCalendar /
// removeTodoFromCalendar, cockpitTasks hasCalendarDay). The server keeps a chosen ToDo in every
// calendar the user connected and follows its changes there; an appointment goes in on its own
// (calendarSynced true) until the user removes it.
import moment from "moment";
import { callAPI } from "./apiUtils";

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

const isFlagOn = (value) => value === true || value === 1 || value === "1" || value === "true";

const isDone = (todo) => todo.status === "completed" || todo.status === "confirmed";

/** The day of an appointment bookmark (filed under "Termine" with its date), or "". */
function appointmentDay(todo) {
  if (!todo || !isFlagOn(todo.isBookmarked) || todo.bookmarkCategory !== "appointment") return "";
  const day = String(todo.bookmarkDeadline || "").slice(0, 10);
  return DAY_RE.test(day) && moment(day, "YYYY-MM-DD", true).isValid() ? day : "";
}

/**
 * A ToDo a calendar can show (server calendarKind): a dated task at its time, an appointment
 * bookmark all day on its date. Any other bookmark has no day yet.
 */
export function hasCalendarDay(todo) {
  if (!todo) return false;
  if (appointmentDay(todo)) return true;
  return !isFlagOn(todo.isBookmarked) && !!todo.createdAt;
}

/**
 * Whether a ToDo gets the calendar button, and how: null (a done task not in the calendars, or a
 * server that does not say `calendarSynced`), else { added, needsDate } — an appointment starts
 * added, a bookmark without a day asks for one first.
 */
export function calendarOptionFor(todo) {
  if (!todo || typeof todo.calendarSynced !== "boolean") return null;
  if (!todo.calendarSynced && isDone(todo)) return null;
  const needsDate = !todo.calendarSynced && !hasCalendarDay(todo);
  if (needsDate && !isFlagOn(todo.isBookmarked)) return null;
  return { added: todo.calendarSynced, needsDate };
}

/**
 * POST /todo/:id/calendar. Resolves { status: true, data: { calendars, added, failed } } or
 * { status: false, code, message } (code: no_date, no_calendar, not_found).
 */
export async function addTodoToCalendar(todoId) {
  if (!todoId) return { status: false, code: "not_found" };
  try {
    const { data } = await callAPI(`/todo/${todoId}/calendar`, {}, "POST", {});
    if (data && data.status) return { status: true, data: data.data || null };
    return { status: false, code: data && data.code, message: data && data.message };
  } catch (e) {
    const body = e && e.response && e.response.data;
    return { status: false, code: body && body.code, message: body && body.message };
  }
}

/** DELETE /todo/:id/calendar: out of all the user's calendars. */
export async function removeTodoFromCalendar(todoId) {
  if (!todoId) return { status: false, code: "not_found" };
  try {
    const { data } = await callAPI(`/todo/${todoId}/calendar`, {}, "DELETE");
    if (data && data.status) return { status: true };
    return { status: false, code: data && data.code, message: data && data.message };
  } catch (e) {
    const body = e && e.response && e.response.data;
    return { status: false, code: body && body.code, message: body && body.message };
  }
}
